/**
 * Rück-Parse-Seam für strukturierte Merkmale der Immobilien-Erfassung.
 *
 * Hintergrund: Beim Speichern schreibt `NewRiskPage` verschachtelte Antworten per
 * `JSON.stringify` in `RiskCharacteristics.riskCharacteristicValue`. Beim Laden
 * liefert `characteristicsToAnswers` (`src/utils/riskCharacteristics.ts`) aber ein
 * flaches `Record<string, string>` **ohne** `JSON.parse`. Im Edit-Modus kommen
 * strukturierte Antworten dadurch als JSON-String zurück — `einheiten.map(...)`
 * kracht, und die Zusammenfassung zeigt die Zeichenlänge des JSON-Strings statt der
 * Anzahl Einheiten.
 *
 * Dieses Modul ist die **eine** Stelle, an der das rückgängig gemacht wird. Der
 * Speicherpfad bleibt unverändert: `JSON.parse` → `JSON.stringify` ergibt denselben
 * Text, ein unverändert erneut gespeichertes Risiko erzeugt also byte-gleiche
 * `characteristics`.
 */

/**
 * Antwort-Keys, die heute strukturiert (Objekt/Array) gespeichert werden — rein
 * dokumentierend für Review und Tests. Dazu kommen alle Keys mit dem Präfix
 * `sicherheit_` (je Sicherheitsanlage ein Objekt).
 *
 * Der Parser unten wertet diese Liste **bewusst nicht** aus: er arbeitet
 * key-unabhängig, damit jede künftige strukturierte Frage automatisch mitläuft.
 * Der globale Antwort-Bag (`street`/`postalCode`/`city`/`country`) ist rein skalar.
 */
export const STRUCTURED_ANSWER_KEYS = [
  "einheiten",
  "branchen",
  "heizungen",
  "umwelt",
  "sonderausstattungen",
  "wellness",
  "unterlagen",
  "stellplatzArten",
  "stellplatzDetails",
  "schaeden",
] as const;

/** Ein „echtes" Objekt — kein Array, kein null. */
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Parst einen String zurück, wenn er wie JSON-Objekt/-Array aussieht. Alles andere
 * (Zahlen, Booleans, Freitext, bereits geparste Werte) bleibt unangetastet; ein
 * kaputter JSON-String fällt auf den Rohwert zurück.
 */
const parseIfStructured = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
};

/**
 * Wandelt einen flachen Antwort-Bag aus dem Backend zurück in die Form, die das
 * Step-Formular erwartet. Idempotent: bereits geparste Bags kommen unverändert
 * zurück, doppelte Anwendung ist schadlos.
 */
export const parseStructuredAnswers = (
  answers: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  if (!answers) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(answers)) {
    out[key] = parseIfStructured(value);
  }
  return out;
};

/**
 * Typisierter Zugriff auf eine Listen-Antwort (z. B. `einheiten`, `unterlagen`).
 * Nimmt Arrays direkt, parst JSON-Strings nach und liefert sonst den Fallback —
 * idempotent, also auch nach `parseStructuredAnswers` gefahrlos aufrufbar.
 */
export const asArray = <T>(value: unknown, fallback: T[] = []): T[] => {
  const parsed = parseIfStructured(value);
  return Array.isArray(parsed) ? (parsed as T[]) : fallback;
};

/**
 * Typisierter Zugriff auf eine Objekt-von-Objekten-Antwort (z. B.
 * `sonderausstattungen`: `{ einbaukuechen: { selected: "true", wert: "324234" } }`).
 * Einträge, die kein Objekt sind, werden verworfen — damit fällt auch das
 * Korruptions-Muster `{ 0: "{", 1: "\"", … }` heraus, das entsteht, wenn ein
 * JSON-String versehentlich per Spread in ein Objekt gekippt wird. Idempotent.
 */
export const asRecordOfRecords = (
  value: unknown,
): Record<string, Record<string, unknown>> => {
  const parsed = parseIfStructured(value);
  if (!isPlainObject(parsed)) return {};
  const out: Record<string, Record<string, unknown>> = {};
  for (const [key, entry] of Object.entries(parsed)) {
    if (isPlainObject(entry)) out[key] = entry;
  }
  return out;
};
